import { type Component, type JSX, Show, splitProps } from "solid-js";
import { twMerge } from "tailwind-merge";

interface GradientBorderProps extends JSX.HTMLAttributes<HTMLDivElement> {
  /**
   * Background color for the inner content. Defaults to "bg-panel".
   * Use semantic tokens like "bg-canvas", "bg-panel", etc.
   */
  bgColor?: string;
  /**
   * Border radius class. Defaults to "rounded-lg".
   */
  rounded?: string;
  /**
   * Additional classes for the outer wrapper (the gradient container).
   */
  wrapperClass?: string;
  /**
   * When true, the gradient expands on hover.
   * Uses CSS @property for smooth animation.
   */
  expandOnHover?: boolean;
  /**
   * When true, uses a more subdued gradient for lists with many items.
   */
  subtle?: boolean;
  /**
   * Custom accent gradient for the border. Pass a full CSS gradient string.
   * Used when `showAccent` is true or on hover when `showAccentOnHover` is true.
   */
  accentGradient?: string;
  /**
   * When true, shows the accent gradient instead of the default gradient.
   */
  showAccent?: boolean;
  /**
   * When true, shows the accent gradient on hover (CSS :hover).
   */
  showAccentOnHover?: boolean;
  /**
   * When true, adds a blurred glow effect when the accent gradient is active.
   */
  glowOnAccent?: boolean;
  children: JSX.Element;
}

// Default gradients
const DEFAULT_LIGHT = "radial-gradient(at 30% 10%, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.15) 100%)";
const DEFAULT_DARK =
  "radial-gradient(at 30% 10%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.1) 100%)";
const SUBTLE_LIGHT = "radial-gradient(at 30% 10%, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.1) 100%)";
const SUBTLE_DARK =
  "radial-gradient(at 30% 10%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.1) 40%, rgba(255,255,255,0.08) 100%)";

// Expand on hover variants (using CSS custom properties)
const DEFAULT_LIGHT_EXPAND =
  "radial-gradient(at 30% 10%, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.08) var(--gradient-border-mid), rgba(0,0,0,0.15) var(--gradient-border-end))";
const DEFAULT_DARK_EXPAND =
  "radial-gradient(at 30% 10%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) var(--gradient-border-mid), rgba(255,255,255,0.1) var(--gradient-border-end))";
const SUBTLE_LIGHT_EXPAND =
  "radial-gradient(at 30% 10%, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.05) var(--gradient-border-mid), rgba(0,0,0,0.1) var(--gradient-border-end))";
const SUBTLE_DARK_EXPAND =
  "radial-gradient(at 30% 10%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.1) var(--gradient-border-mid), rgba(255,255,255,0.08) var(--gradient-border-end))";

/**
 * A wrapper component that creates a 1px gradient border effect.
 *
 * Uses the wrapper technique: outer div has gradient background,
 * inner div has solid background with 1px padding to reveal the gradient as a border.
 *
 * Supports accent gradients with hover states, and animated expansion.
 * The `expandOnHover` animation relies on the `gradient-border-*` classes and
 * `--gradient-border-mid/end` @property declarations in the host app's global
 * stylesheet (both bluesky and chief ship them).
 */
export const GradientBorder: Component<GradientBorderProps> = (props) => {
  const [local, rest] = splitProps(props, [
    "bgColor",
    "rounded",
    "wrapperClass",
    "expandOnHover",
    "subtle",
    "accentGradient",
    "showAccent",
    "showAccentOnHover",
    "glowOnAccent",
    "children",
    "class",
  ]);

  const bgColor = () => local.bgColor ?? "bg-panel";
  const rounded = () => local.rounded ?? "rounded-lg";

  // Determine if we're using accent mode
  const hasAccent = () => local.accentGradient && (local.showAccent || local.showAccentOnHover);

  // Whether glow should be visible (accent is active, not just on hover)
  const showGlow = () => local.glowOnAccent && local.accentGradient && local.showAccent;

  // Whether glow should appear on hover
  const showGlowOnHover = () =>
    local.glowOnAccent && local.accentGradient && local.showAccentOnHover && !local.showAccent;

  // Get the appropriate default gradient based on subtle/expandOnHover
  const getDefaultGradient = (isDark: boolean) => {
    if (local.subtle) {
      return local.expandOnHover
        ? isDark
          ? SUBTLE_DARK_EXPAND
          : SUBTLE_LIGHT_EXPAND
        : isDark
          ? SUBTLE_DARK
          : SUBTLE_LIGHT;
    }
    return local.expandOnHover
      ? isDark
        ? DEFAULT_DARK_EXPAND
        : DEFAULT_LIGHT_EXPAND
      : isDark
        ? DEFAULT_DARK
        : DEFAULT_LIGHT;
  };

  // The main gradient to show
  const mainGradient = () => getDefaultGradient(false);
  const mainGradientDark = () => getDefaultGradient(true);

  // For accent hover, we use CSS custom properties set via inline style
  const wrapperStyle = (): JSX.CSSProperties => {
    if (hasAccent()) {
      return {
        "--gradient-main": mainGradient(),
        "--gradient-main-dark": mainGradientDark(),
        "--gradient-accent": local.accentGradient,
      };
    }
    return {};
  };

  // Build class for accent hover behavior
  const accentClass = () => {
    if (local.showAccent && local.accentGradient) {
      return "gradient-border-accent-active";
    }
    if (local.showAccentOnHover && local.accentGradient) {
      return "gradient-border-accent-hover";
    }
    return "";
  };

  // Determine if we need Tailwind gradient classes (not using CSS-based hover)
  const needsTailwindGradient = () => !hasAccent();

  // Light mode gradient classes (only when using defaults)
  const lightGradientClass = () => {
    if (!needsTailwindGradient()) return "";
    if (local.subtle) {
      return local.expandOnHover
        ? "bg-[radial-gradient(at_30%_10%,_rgba(0,0,0,0.03)_0%,_rgba(0,0,0,0.05)_var(--gradient-border-mid),_rgba(0,0,0,0.1)_var(--gradient-border-end))]"
        : "bg-[radial-gradient(at_30%_10%,_rgba(0,0,0,0.03)_0%,_rgba(0,0,0,0.05)_40%,_rgba(0,0,0,0.1)_100%)]";
    }
    return local.expandOnHover
      ? "bg-[radial-gradient(at_30%_10%,_rgba(0,0,0,0.05)_0%,_rgba(0,0,0,0.08)_var(--gradient-border-mid),_rgba(0,0,0,0.15)_var(--gradient-border-end))]"
      : "bg-[radial-gradient(at_30%_10%,_rgba(0,0,0,0.05)_0%,_rgba(0,0,0,0.08)_40%,_rgba(0,0,0,0.15)_100%)]";
  };

  // Dark mode gradient classes (only when using defaults)
  const darkGradientClass = () => {
    if (!needsTailwindGradient()) return "";
    if (local.subtle) {
      return local.expandOnHover
        ? "dark:bg-[radial-gradient(at_30%_10%,_rgba(255,255,255,0.18)_0%,_rgba(255,255,255,0.1)_var(--gradient-border-mid),_rgba(255,255,255,0.08)_var(--gradient-border-end))]"
        : "dark:bg-[radial-gradient(at_30%_10%,_rgba(255,255,255,0.18)_0%,_rgba(255,255,255,0.1)_40%,_rgba(255,255,255,0.08)_100%)]";
    }
    return local.expandOnHover
      ? "dark:bg-[radial-gradient(at_30%_10%,_rgba(255,255,255,0.35)_0%,_rgba(255,255,255,0.15)_var(--gradient-border-mid),_rgba(255,255,255,0.1)_var(--gradient-border-end))]"
      : "dark:bg-[radial-gradient(at_30%_10%,_rgba(255,255,255,0.35)_0%,_rgba(255,255,255,0.15)_40%,_rgba(255,255,255,0.1)_100%)]";
  };

  // If we need glow, wrap in a relative container
  const needsGlowWrapper = () => local.glowOnAccent && local.accentGradient;

  const borderElement = (
    <div
      class={twMerge(
        "p-px",
        rounded(),
        local.expandOnHover && "gradient-border-hover",
        hasAccent() && "gradient-border-with-accent",
        accentClass(),
        lightGradientClass(),
        darkGradientClass(),
        local.wrapperClass
      )}
      style={wrapperStyle()}
    >
      <div class={twMerge(rounded(), bgColor(), "overflow-hidden", local.class)} {...rest}>
        {local.children}
      </div>
    </div>
  );

  return (
    <Show when={needsGlowWrapper()} fallback={borderElement}>
      <div class="relative">
        {/* Glow layer - blurred duplicate behind the border */}
        <div
          class={twMerge(
            "pointer-events-none absolute inset-0 p-px transition-opacity duration-300",
            rounded(),
            showGlow() ? "opacity-75" : "opacity-0",
            showGlowOnHover() && "gradient-border-glow-hover"
          )}
          style={{
            background: local.accentGradient,
            filter: "blur(10px)",
          }}
          aria-hidden="true"
        />
        {/* Main border element */}
        {borderElement}
      </div>
    </Show>
  );
};
